import { useMemo, type ComponentType, type ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Building2, MapPin, Phone, Mail, User, Calendar, Ban, Wallet, Router, Home, Target,
} from 'lucide-react';
import { formatarCNPJ, formatarTelefone } from '@/utils/formatters';
import { ClickToCall } from '@/components/common/ClickToCall';
import { PhoneWithOperator } from '@/components/ui/phone-with-operator';
import { useCoberturaClaroByCep } from '@/hooks/useParqueVivoByCnpj';
import type { LeadCarteira } from '@/hooks/useLeadsCarteira';

/**
 * A ficha do lead OI.
 *
 * Cliente de fibra da Oi bloqueado ou suspenso, quase tudo por inadimplência.
 * O gancho da ligação já vem pronto na lista, e é por ele que a ficha abre: o
 * serviço está parado desde tal dia e ele paga tanto.
 *
 * ⚠️ Três decisões de desenho, todas do Douglas em 09/09/2026:
 *
 * 1. A COBERTURA MORA NO ENDEREÇO. Não existe caixa de cobertura separada — o
 *    consultor lê o endereço e vê ali mesmo se dá para vender. Um card à parte
 *    obrigava a casar de cabeça três linhas com dois endereços.
 *
 * 2. SÃO DOIS ENDEREÇOS, JUNTOS. A lista da Oi manda onde a fibra ESTÁ; o da
 *    empresa vem da Receita na importação. Quando são lugares diferentes, a
 *    fibra está na casa e a empresa segue sem — cabe uma segunda venda. Medido
 *    na lista de 09/09: 1.421 de 2.005 empresas (71%) em lugar diferente, e 853
 *    delas com cobertura Claro no endereço da empresa.
 *
 * 3. TITULAR DA OI NÃO É SÓCIO DA EMPRESA. O titular é quem atende o telefone,
 *    e por isso vive no bloco "Como falar", junto do número. Sócio é dado
 *    cadastral e fica com a empresa. Misturar os dois faz o consultor perguntar
 *    pela pessoa errada.
 */

interface LeadDados360OiProps {
  lead: LeadCarteira;
  onLigar: (telefone: string) => void;
  campanhaNome?: string | null;
  variante?: 'OI' | 'ALGAR';
}

function InfoRow({ label, value, icon: Icon }: {
  label: string;
  value?: string | number | null;
  icon?: ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        {Icon && <Icon className="w-4 h-4" />}
        <span>{label}</span>
      </div>
      <span className="text-sm font-medium text-right max-w-[60%] truncate" title={value ? String(value) : undefined}>
        {value || <span className="text-muted-foreground/50">—</span>}
      </span>
    </div>
  );
}

/** O cabeçalho de um endereço: o que é o lugar, e se lá tem cobertura. */
function TituloEndereco({ titulo, fonte, cobertura }: {
  titulo: string;
  fonte?: string;
  cobertura: ReactNode;
}) {
  return (
    <div className="mb-1.5 flex items-center gap-2">
      <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{titulo}</span>
      {fonte && <span className="text-[9px] text-muted-foreground/70">{fonte}</span>}
      <span className="ml-auto">{cobertura}</span>
    </div>
  );
}

function SeloCobertura({ tem, detalhe }: { tem: boolean | null; detalhe?: string | null }) {
  if (tem === null) return <span className="text-[10px] text-muted-foreground/50">cobertura não consultada</span>;
  if (!tem) return <span className="text-[10px] font-bold tracking-wider text-primary">SEM COBERTURA</span>;
  return (
    <span className="text-[10px] font-bold tracking-wider">
      COM COBERTURA{detalhe ? ` · ${detalhe}` : ''}
    </span>
  );
}

function formatarCPF(v?: string | null): string {
  const d = String(v || '').replace(/\D/g, '');
  if (d.length !== 11) return String(v || '');
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

// `onLigar` fica na interface porque o modal passa, mas nao e' repassado ao
// ClickToCall: igual ao Algar e ao TT, quem disca e' o proprio componente.
export function LeadDados360Oi({ lead: registro, campanhaNome, variante = 'OI' }: LeadDados360OiProps) {
  const algar = variante === 'ALGAR';
  const dd = (registro.dados_discadora || {}) as Record<string, unknown>;
  const texto = (v: unknown): string | null => {
    const t = String(v ?? '').trim();
    return t.length ? t : null;
  };
  // O importador preserva a linha Oi em dados_discadora. Campos ainda não
  // projetados em colunas continuam disponíveis ali, sem substituir edições.
  const lead = {
    ...registro,
    nome_proprietario: registro.nome_proprietario || texto(dd.nome_proprietario),
    plano_atual: registro.plano_atual || texto(dd.plano_atual),
    conta_operadora: registro.conta_operadora || texto(dd.conta_operadora),
    segmento: registro.segmento || texto(dd.segmento),
    cobertura_fibra: registro.cobertura_fibra || texto(dd.cobertura_fibra),
    venda_logradouro: registro.venda_logradouro || texto(dd.venda_logradouro),
    venda_numero: registro.venda_numero || texto(dd.venda_numero),
    venda_complemento: registro.venda_complemento || texto(dd.venda_complemento),
    venda_bairro: registro.venda_bairro || texto(dd.venda_bairro),
    venda_cidade: registro.venda_cidade || texto(dd.venda_cidade),
    venda_uf: registro.venda_uf || texto(dd.venda_uf),
    venda_cep: registro.venda_cep || texto(dd.venda_cep),
  };

  const ehPJ = lead.tipo_pessoa !== 'PF' && !!lead.cnpj;
  const cpfTitular = lead.cpf || lead.doc_cpf || texto(dd.doc_cpf);

  const cepInstalacao = lead.venda_cep || texto(dd.cep) || '';
  const cepEmpresa = texto(dd.empresa_cep) || '';
  const { data: coberturaInstalacao, isLoading: carregandoInstalacao } = useCoberturaClaroByCep(cepInstalacao);
  const { data: coberturaEmpresa } = useCoberturaClaroByCep(
    cepEmpresa && cepEmpresa !== cepInstalacao ? cepEmpresa : null,
  );

  // A lista traz o carimbo do fornecedor; a base da Claro é a nossa. Nos 149
  // CEPs conferidos elas bateram em 146 — e são os 3 que sobram que decidem se
  // vale mandar o técnico. Por isso a divergência é dita, não escondida.
  const carimbo = String(lead.cobertura_fibra ?? '').toUpperCase();
  const listaDizSim = carimbo.includes('COM');
  const listaDisse = carimbo.includes('COM') || carimbo.includes('SEM');
  const divergem = listaDisse && !carregandoInstalacao && !!coberturaInstalacao
    && listaDizSim !== coberturaInstalacao.coberto;

  const bloqueio = useMemo(() => ({
    motivo: texto(dd.oi_motivo_bloqueio),
    tipo: texto(dd.oi_tipo_bloqueio),
    desde: texto(dd.oi_data_bloqueio),
  }), [dd.oi_motivo_bloqueio, dd.oi_tipo_bloqueio, dd.oi_data_bloqueio]);

  const diasParado = useMemo(() => {
    if (!bloqueio.desde) return null;
    const [d, m, a] = bloqueio.desde.split('/');
    const data = new Date(Number(a), Number(m) - 1, Number(d));
    if (Number.isNaN(data.getTime())) return null;
    return Math.max(0, Math.floor((Date.now() - data.getTime()) / 86_400_000));
  }, [bloqueio.desde]);

  const telefones = [...new Set((algar && dd.algar_layout_atual ? [texto(dd.telefone_titular)] : [lead.telefone, lead.telefone_2, lead.telefone_3, lead.telefone_4])
    .filter((t): t is string => !!t && String(t).replace(/\D/g, '').length >= 10))];

  // O quadro societário vem da Receita, com o cargo e a data de entrada, e o
  // administrador na frente — é com ele que se fala. A lista da Oi só mandava
  // nomes crus separados por ";", e em 351 linhas de 5.054; fica de reserva.
  const socios = useMemo(() => {
    const daReceita = dd.socios_receita;
    if (Array.isArray(daReceita) && daReceita.length) {
      return (daReceita as { nome: string; qualificacao?: string | null; desde?: string | null; administrador?: boolean }[])
        .filter((s) => s?.nome);
    }
    const bruto = String(dd.socios ?? '').trim();
    if (!bruto) return [];
    return bruto.split(';').map((n) => n.trim()).filter(Boolean)
      .map((nome) => ({ nome, qualificacao: null, desde: null, administrador: false }));
  }, [dd.socios_receita, dd.socios]);

  const telefonesEmpresa = [texto(dd.empresa_telefone), texto(dd.empresa_telefone_2)]
    .filter((t): t is string => !!t);
  const emailEmpresa = texto(dd.empresa_email) || (algar ? texto(dd.email_receita) : null);
  const nomeFantasia = lead.nome_fantasia || texto(dd.empresa_nome_fantasia);

  const enderecoInstalacao = [lead.venda_logradouro, lead.venda_numero].filter(Boolean).join(', ');

  const LUGAR: Record<string, { rotulo: string; oportunidade: boolean }> = {
    MESMO: { rotulo: algar ? 'endereço solicitado coincide com o da empresa' : 'a fibra está no endereço da empresa', oportunidade: false },
    MESMA_RUA: { rotulo: 'mesma rua da empresa', oportunidade: false },
    MESMA_CIDADE: { rotulo: 'mesma cidade, outro endereço', oportunidade: true },
    OUTRA_CIDADE: { rotulo: 'outra cidade', oportunidade: true },
    OUTRO_ESTADO: { rotulo: 'outro estado', oportunidade: true },
    INDEFINIDO: { rotulo: 'não deu para comparar', oportunidade: false },
  };
  const lugar = lead.venda_relacao ? LUGAR[lead.venda_relacao] : null;
  const segundaFibra = !algar && ehPJ && !!lead.endereco_empresa
    && (lugar?.oportunidade === true || lead.venda_residencial === true);

  return (
    <div className="space-y-3">
      {/* POR QUE LIGAR — o gancho já vem pronto da lista */}
      <Card className="border-l-[3px] border-l-primary rounded-l-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Ban className="w-4 h-4" /> Por que ligar
          </CardTitle>
        </CardHeader>
        <CardContent>
          {algar ? <>
            <p className="text-sm leading-relaxed">{String(dd.origem_mailing || campanhaNome || lead.campanha_nome).toUpperCase().includes('URA') ? 'Cliente entrou em contato com a URA da Algar.' : 'Cliente solicitou proposta na Algar.'} Confirme a necessidade atual: a solicitação não comprova contratação.</p>
            <div className="grid md:grid-cols-2 gap-x-6 mt-2 pt-2 border-t">
              <InfoRow label="Status na Algar" value={texto(dd.algar_status)} />
              <InfoRow label="Substatus" value={texto(dd.algar_substatus)} />
              <InfoRow label="Produto solicitado" value={texto(dd.produto)} />
              <InfoRow label="Tipo de produto" value={texto(dd.tipo_produto)} />
              <InfoRow label="Solicitado em" value={texto(dd.algar_criacao)} />
              <InfoRow label="Canal" value={texto(dd.algar_canal)} />
              <InfoRow label="Motivos" value={Array.isArray(dd.algar_motivos) ? dd.algar_motivos.join(' · ') : null} />
              <InfoRow label="Campanha" value={campanhaNome || lead.campanha_nome} />
            </div>
          </> : <>
          <p className="text-sm leading-relaxed">
            Cliente de <span className="font-semibold">fibra da Oi</span> com o serviço{' '}
            <span className="font-semibold">{(bloqueio.tipo || 'parado').toLowerCase()}</span>
            {bloqueio.desde && <> desde <span className="font-semibold">{bloqueio.desde}</span></>}
            {diasParado !== null && diasParado > 0 && (
              <span className="text-muted-foreground"> ({diasParado} dias)</span>
            )}
            .
          </p>
          <div className="grid md:grid-cols-2 gap-x-6 mt-2 pt-2 border-t">
            <InfoRow label="Motivo na Oi" value={bloqueio.motivo} />
            <InfoRow
              label="Paga hoje na Oi"
              value={lead.valor_fatura_atual != null
                ? Number(lead.valor_fatura_atual).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                : null}
              icon={Wallet}
            />
            <InfoRow label="Plano que ele tem" value={lead.plano_atual} icon={Router} />
            <InfoRow label="Campanha" value={campanhaNome || lead.campanha_nome} icon={Calendar} />
          </div>
          </>}
        </CardContent>
      </Card>

      {/* A SEGUNDA VENDA — só nasce quando existe o fato */}
      {segundaFibra && (
        <Card className="border-l-[3px] border-l-primary rounded-l-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Target className="w-4 h-4" /> Cabe uma segunda fibra
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">
              A fibra da Oi está{lead.venda_residencial ? ' na moradia dele' : ' num endereço'}
              {lead.venda_cidade ? `, em ${lead.venda_cidade}` : ''}
              {lugar?.oportunidade ? ` — ${lugar.rotulo} da empresa` : ''}
              {coberturaEmpresa?.coberto
                ? ', e no endereço da empresa TEM cobertura Claro.'
                : '.'}
              {' '}Além de trocar a fibra que está parada, dá para vender uma segunda onde a empresa é.
            </p>
          </CardContent>
        </Card>
      )}

      {/* OS DOIS ENDEREÇOS — cada um com a sua cobertura colada */}
      <Card className="border-l-[3px] border-l-brand-rose rounded-l-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Onde o serviço está
          </CardTitle>
        </CardHeader>
        <CardContent>
          <TituloEndereco
            titulo={algar ? "Endereço solicitado pelo cliente" : "A fibra da Oi está aqui"}
            cobertura={
              <SeloCobertura
                tem={carregandoInstalacao ? null : (coberturaInstalacao?.coberto ?? (listaDisse ? listaDizSim : null))}
                detalhe={coberturaInstalacao?.tipo_servico}
              />
            }
          />
          {enderecoInstalacao ? (
            <p className="text-sm leading-relaxed">
              <span className="font-semibold">{enderecoInstalacao}</span>
              {lead.venda_complemento && <><br /><span className="text-muted-foreground">{lead.venda_complemento}</span></>}
              {(lead.venda_bairro || lead.venda_cidade) && (
                <><br /><span className="text-muted-foreground">
                  {[lead.venda_bairro, [lead.venda_cidade, lead.venda_uf].filter(Boolean).join(' — ')]
                    .filter(Boolean).join(' · ')}
                </span></>
              )}
              {lead.venda_cep && <><br /><span className="text-muted-foreground">CEP {lead.venda_cep}</span></>}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground/60">Endereço da instalação não veio na lista.</p>
          )}

          {divergem && (
            <p className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
              <span
                aria-hidden
                className="mt-0.5 inline-block h-3 w-3 shrink-0 rounded-[2px] border border-border"
                style={{ backgroundImage: 'repeating-linear-gradient(135deg,#5B0A0D 0 3px,#B4808C 3px 6px)' }}
              />
              <span>
                A lista diz <span className="font-semibold">{listaDizSim ? 'COM' : 'SEM'} COBERTURA</span> e a
                base da Claro diz o contrário. Confira antes de prometer instalação.
              </span>
            </p>
          )}

          {!algar && <div className="mt-2 pt-2 border-t">
            <InfoRow label="Tipo do imóvel" value={texto(dd.oi_tipo_complemento)} icon={Home} />
            <InfoRow label="Acesso GPON" value={texto(dd.oi_acesso_gpon)} />
            <InfoRow label="Conta na Oi" value={lead.conta_operadora} />
            <InfoRow label="Central da Oi" value={texto(dd.oi_estacao)} />
          </div>}

          {ehPJ && lead.endereco_empresa && (
            <div className="mt-3 pt-3 border-t">
              <TituloEndereco
                titulo="Endereço da empresa"
                fonte="Receita Federal"
                cobertura={
                  !cepEmpresa ? <SeloCobertura tem={null} /> : cepEmpresa !== cepInstalacao
                    ? <SeloCobertura tem={coberturaEmpresa?.coberto ?? null} detalhe={coberturaEmpresa?.tipo_servico} />
                    : <SeloCobertura tem={coberturaInstalacao?.coberto ?? null} detalhe="mesmo CEP da instalação" />
                }
              />
              <p className="text-sm leading-relaxed">{lead.endereco_empresa}</p>
              {lugar && (
                <p className={`mt-1 text-xs ${lugar.oportunidade ? 'font-semibold text-primary' : 'text-muted-foreground'}`}>
                  {lugar.rotulo}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* QUEM É — a empresa, ou a pessoa. Sem titular: titular é contato. */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              {ehPJ ? <Building2 className="w-4 h-4" /> : <User className="w-4 h-4" />} Quem é
            </span>
            <Badge variant="outline" className="text-[9px] font-normal text-muted-foreground">
              {ehPJ ? 'empresa · CNPJ' : 'pessoa · CPF'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm font-semibold leading-relaxed">{lead.razao_social}</p>
          {nomeFantasia && nomeFantasia.toUpperCase() !== String(lead.razao_social || '').toUpperCase() && (
            <p className="text-sm">{nomeFantasia}</p>
          )}
          <p className="text-sm text-muted-foreground">
            {ehPJ ? formatarCNPJ(lead.cnpj || '') : formatarCPF(cpfTitular)}
          </p>
          <div className="grid md:grid-cols-2 gap-x-6 mt-2 pt-2 border-t">
            {!algar && <InfoRow label="Segmento na Oi" value={lead.segmento} />}
            {ehPJ && (
              <>
                <InfoRow label="Situação na Receita" value={texto(dd.situacao_cadastral) || texto(dd.empresa_situacao)} />
                <InfoRow label="Natureza" value={texto(dd.natureza_juridica) || texto(dd.empresa_natureza)} />
                <InfoRow label="CNAE" value={texto(dd.cnae_principal)} />
                <InfoRow label="MEI" value={texto(dd.mei)} />
                <InfoRow label="Simples" value={texto(dd.simples)} />
                <InfoRow label="Porte" value={texto(dd.porte) || texto(dd.empresa_porte)} />
              </>
            )}
          </div>

          {/* O TELEFONE DA EMPRESA. Vem da Receita, não da lista da Oi — que só
              manda o número do titular. Fica aqui, na empresa, e não no "Como
              falar", porque quem atende neste número é a empresa. */}
          {ehPJ && (telefonesEmpresa.length > 0 || emailEmpresa) && (
            <div className="mt-2 pt-2 border-t">
              <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                Contato da empresa
              </p>
              {telefonesEmpresa.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-4">
                  {telefonesEmpresa.map((t) => (
                    <div key={t} className="flex flex-col gap-0.5">
                      <ClickToCall telefone={t} leadId={lead.id}>
                        <span className="font-mono text-sm font-medium text-primary hover:underline cursor-pointer">
                          {formatarTelefone(t)}
                        </span>
                      </ClickToCall>
                      <PhoneWithOperator phone={t} variant="badge-only" />
                    </div>
                  ))}
                </div>
              )}
              {emailEmpresa && <InfoRow label="E-mail" value={emailEmpresa} icon={Mail} />}
            </div>
          )}

          {/* O QUADRO SOCIETÁRIO INTEIRO, com cargo e desde quando. Empresário
              Individual e MEI não têm sócio por natureza — nesses o bloco some. */}
          {ehPJ && socios.length > 0 && (
            <div className="mt-2 pt-2 border-t">
              <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                {socios.length > 1 ? `Sócios da empresa · ${socios.length}` : 'Sócio da empresa'}
              </p>
              <div className="mt-1 space-y-1">
                {socios.map((s) => (
                  <div key={`${s.nome}-${s.desde ?? ''}`} className="flex items-baseline justify-between gap-3">
                    <span className={`text-sm ${s.administrador ? 'font-semibold' : ''}`}>{s.nome}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {[s.qualificacao, s.desde ? `desde ${s.desde}` : null].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* COMO FALAR — o titular da conta e os números dele */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Phone className="w-4 h-4" /> Como falar
          </CardTitle>
        </CardHeader>
        <CardContent>
          {lead.nome_proprietario && (
            <div className="mb-2 pb-2 border-b">
              <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                {algar ? "Titular da solicitação na Algar" : "Titular da conta na Oi"}
              </p>
              <p className="mt-0.5 text-sm font-semibold">{lead.nome_proprietario}</p>
              {cpfTitular && (
                <p className="text-xs text-muted-foreground">CPF {formatarCPF(cpfTitular)}</p>
              )}
            </div>
          )}

          {telefones.length ? (
            <div className="flex flex-wrap gap-4">
              {telefones.map((t) => (
                <div key={t} className="flex flex-col gap-0.5">
                  <ClickToCall telefone={t} leadId={lead.id}>
                    <span className="font-mono text-sm font-medium text-primary hover:underline cursor-pointer">
                      {formatarTelefone(t)}
                    </span>
                  </ClickToCall>
                  <PhoneWithOperator phone={t} variant="badge-only" />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground/60">A lista não trouxe telefone para este cliente.</p>
          )}

          {algar && !!dd.contato && <InfoRow label="Contato informado" value={texto(dd.contato)} />}
          {(algar ? texto(dd.email_atendimento) : lead.email) && (
            <div className="mt-2 pt-2 border-t">
              <InfoRow label={algar ? "E-mail do titular" : ehPJ ? "E-mail na Receita" : "E-mail"} value={algar ? texto(dd.email_atendimento) : lead.email} icon={Mail} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
